import React, { useState, useMemo } from "react";
import { NavLink, Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import {
  LayoutDashboard, Package, Warehouse, ArrowDownToLine, ArrowUpFromLine,
  Shuffle, ScrollText, Building2, Truck, LogOut, Menu, X, Cpu, Rss,
  ShoppingCart, Users, UserCog, CreditCard, Archive, Newspaper, Settings,
  ClipboardList, Mail,
} from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../lib/auth";
import { canOpenRoute } from "../lib/roles";
import { useSystemSettings } from "../lib/systemSettings";
import { usePosLock } from "../lib/posLock";
import BackButton from "./BackButton";
import { useEffect } from "react";

// Polish-Package 2026-08-30: Menü in zwei Gruppen aufgeteilt.
// „Daily Business" = tägliche Kassen-/Lager-Arbeit (oben).
// „ADMINTOOLS"    = Stammdaten- und Systemverwaltung (unten, Divider).
// Reihenfolge bewusst nach Nutzer-Vorgabe. RBAC bleibt unverändert
// (`canOpenRoute` filtert weiterhin admin-only Einträge für Employees).
const DAILY_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/pos", label: "Kasse", icon: ShoppingCart, testId: "nav-pos" },
  { to: "/wareneingang", label: "Wareneingang", icon: ArrowDownToLine, testId: "nav-wareneingang" },
  { to: "/warenausgang", label: "Warenausgang", icon: ArrowUpFromLine, testId: "nav-warenausgang" },
  { to: "/umlagerung", label: "Umlagerung", icon: Shuffle, testId: "nav-umlagerung" },
  { to: "/lagerbestand", label: "Lagerbestand", icon: Warehouse, testId: "nav-lagerbestand" },
  { to: "/produkte", label: "Produkte", icon: Package, testId: "nav-produkte" },
  { to: "/buchungshistorie", label: "Historie", icon: ScrollText, testId: "nav-buchungshistorie" },
  { to: "/listen-belege", label: "Arbeitslisten", icon: ClipboardList, testId: "nav-listen-belege" },
];

const ADMIN_NAV = [
  { to: "/lieferanten", label: "Lieferanten", icon: Truck, testId: "nav-lieferanten" },
  { to: "/kunden", label: "Kunden", icon: Users, testId: "nav-kunden" },
  { to: "/lagerorte", label: "Lagerorte", icon: Building2, testId: "nav-lagerorte", adminOnly: true },
  { to: "/automaten", label: "Automaten", icon: Cpu, testId: "nav-automaten" },
  { to: "/kassen", label: "Kassen", icon: CreditCard, testId: "nav-kassen", adminOnly: true },
  { to: "/integrationen", label: "Integrationen", icon: Rss, testId: "nav-integrationen" },
  { to: "/kundenportal", label: "Kundenportal", icon: Newspaper, testId: "nav-kundenportal", adminOnly: true },
  { to: "/benutzer", label: "Benutzer", icon: UserCog, testId: "nav-benutzer" },
  { to: "/archiv", label: "Archiv", icon: Archive, testId: "nav-archiv", adminOnly: true },
  { to: "/einstellungen", label: "Einstellungen", icon: Settings, testId: "nav-einstellungen", adminOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { settings, brandName } = useSystemSettings();
  const companyName = brandName;
  const logoUrl = settings?.logo_data_url || null;
  const visibleDaily = useMemo(
    () => DAILY_NAV.filter((item) => canOpenRoute(user, item.to)),
    [user]
  );
  const visibleAdmin = useMemo(
    () => ADMIN_NAV.filter((item) => canOpenRoute(user, item.to)),
    [user]
  );
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isLocked } = usePosLock();

  // Wenn POS Lock Mode aktiv ist, darf ausschließlich die Kasse offen sein.
  // Ein Route-Wechsel via URL wird sofort umgelenkt — der Lock-Zustand kann
  // NICHT über die Browser-Adresszeile umgangen werden.
  useEffect(() => {
    if (isLocked && location.pathname !== "/pos") {
      navigate("/pos", { replace: true });
    }
  }, [isLocked, location.pathname, navigate]);

  const handleLogout = async () => {
    if (isLocked) return; // Logout im Lock Mode gesperrt
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-[100dvh] bg-zinc-950 text-zinc-100 flex" data-pos-locked={isLocked ? "1" : "0"}>
      {/* POS Lock Mode: Sidebar + Mobile Top Bar komplett ausgeblendet.
          Owner-Fix (05.09.2026): der frühere floating Entsperren-Button
          oben rechts wurde ersatzlos entfernt. Es gibt genau EINEN
          Lock/Unlock-Control: den funktionalen Button neben dem KASSE-
          Header (siehe POS.jsx). Kein zweites floating/absolute Control. */}
      {!isLocked && (
      <aside className="hidden lg:flex flex-col w-64 border-r border-zinc-800 bg-zinc-950 sticky top-0 h-screen">
        <div className="px-5 py-6 border-b border-zinc-800">
          <Link
            to="/"
            data-testid="brand-home-link"
            aria-label="Zum Dashboard"
            className="block cursor-pointer group"
          >
            {logoUrl ? (
              <img src={logoUrl} alt="Zum Dashboard" data-testid="brand-logo" className="h-10 max-w-full object-contain" />
            ) : (
              <div className="font-display text-2xl font-black tracking-tight text-cyan-400 uppercase group-hover:text-cyan-300 transition-colors" data-testid="brand-name">
                {companyName}
              </div>
            )}
          </Link>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto min-h-0" data-testid="sidebar-nav">
          {visibleDaily.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-3 text-sm border-l-2 transition-colors ${
                  isActive
                    ? "border-cyan-400 bg-zinc-900 text-cyan-400"
                    : "border-transparent text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900"
                }`
              }
            >
              <item.icon size={18} />
              <span className="uppercase tracking-wide font-medium">{item.label}</span>
            </NavLink>
          ))}
          {visibleAdmin.length > 0 && (
            <div
              data-testid="sidebar-admintools-divider"
              className="mt-4 mb-1 px-5 pt-4 border-t border-zinc-800 text-[10px] tracking-[0.28em] uppercase text-zinc-500 font-semibold"
            >
              Admintools
            </div>
          )}
          {visibleAdmin.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-3 text-sm border-l-2 transition-colors ${
                  isActive
                    ? "border-cyan-400 bg-zinc-900 text-cyan-400"
                    : "border-transparent text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900"
                }`
              }
            >
              <item.icon size={18} />
              <span className="uppercase tracking-wide font-medium">{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-zinc-800 p-4">
          <div className="text-xs text-zinc-500 uppercase tracking-wider">Angemeldet</div>
          <div className="text-sm truncate mt-1" data-testid="current-user-email">{user?.email}</div>
          <div className="text-xs text-cyan-400 uppercase mt-1">{user?.role}</div>
          <Button
            data-testid="logout-btn"
            variant="outline"
            size="sm"
            className="w-full mt-3 border-zinc-700 hover:bg-zinc-800 rounded-sm"
            onClick={handleLogout}
          >
            <LogOut size={14} className="mr-2" /> Abmelden
          </Button>
        </div>
      </aside>
      )}

      {/* Mobile top bar — im Lock Mode ebenfalls versteckt */}
      {!isLocked && (
      <div
        className="lg:hidden fixed top-0 left-0 right-0 z-30 border-b border-zinc-800 bg-zinc-950 flex items-center justify-between px-4 py-3"
        style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}
      >
        <Link
          to="/"
          data-testid="brand-home-link-mobile"
          aria-label="Zum Dashboard"
          className="inline-flex items-center min-w-0"
        >
          {logoUrl ? (
            <img src={logoUrl} alt="Zum Dashboard" data-testid="brand-logo-mobile" className="h-8 max-w-[160px] object-contain" />
          ) : (
            <div className="font-display text-lg font-black tracking-tight text-cyan-400 uppercase truncate">
              {companyName}
            </div>
          )}
        </Link>
        <Button
          data-testid="mobile-menu-btn"
          variant="ghost"
          size="icon"
          onClick={() => setMobileOpen(true)}
          className="text-zinc-100"
        >
          <Menu />
        </Button>
      </div>
      )}

      {/* Mobile drawer — vertically flex, header sticky, nav scrolls, footer sticky */}
      {mobileOpen && !isLocked && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-zinc-950 flex flex-col"
          style={{ height: "100dvh" }}
          data-testid="mobile-drawer"
        >
          <div
            className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 shrink-0"
            style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}
          >
            <div className="font-display text-lg font-black tracking-tight text-cyan-400 uppercase">
              Menü
            </div>
            <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)} className="text-zinc-100">
              <X />
            </Button>
          </div>
          <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain" data-testid="mobile-drawer-nav">
            {visibleDaily.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`m-${item.testId}`}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-4 border-b border-zinc-900 ${
                    isActive ? "text-cyan-400 bg-zinc-900" : "text-zinc-200"
                  }`
                }
              >
                <item.icon size={20} />
                <span className="uppercase tracking-wide">{item.label}</span>
              </NavLink>
            ))}
            {visibleAdmin.length > 0 && (
              <div
                data-testid="m-sidebar-admintools-divider"
                className="mt-2 px-5 pt-4 border-t border-zinc-800 text-[10px] tracking-[0.28em] uppercase text-zinc-500 font-semibold"
              >
                Admintools
              </div>
            )}
            {visibleAdmin.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`m-${item.testId}`}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-4 border-b border-zinc-900 ${
                    isActive ? "text-cyan-400 bg-zinc-900" : "text-zinc-200"
                  }`
                }
              >
                <item.icon size={20} />
                <span className="uppercase tracking-wide">{item.label}</span>
              </NavLink>
            ))}
          </nav>
          <div
            className="p-4 border-t border-zinc-800 shrink-0"
            style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
          >
            <div className="text-xs text-zinc-500 mb-2 uppercase truncate">{user?.email}</div>
            <Button
              data-testid="m-logout-btn"
              onClick={handleLogout}
              variant="outline"
              className="w-full border-zinc-700 rounded-sm"
            >
              <LogOut size={16} className="mr-2" /> Abmelden
            </Button>
          </div>
        </div>
      )}

      <main
        className={`flex-1 min-w-0 flex flex-col ${isLocked ? "pt-0" : "pt-14 lg:pt-0"}`}
        style={isLocked ? {} : { paddingTop: "calc(3.5rem + env(safe-area-inset-top))" }}
      >
        <div className={`flex-1 w-full ${isLocked ? "max-w-none px-2 sm:px-4 py-3" : "max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 lg:py-8"}`}>
          {!isLocked && <BackButton className="mb-3" />}
          <Outlet />
        </div>
        {!isLocked && (
        <footer
          data-testid="global-footer"
          className="w-full text-center text-[11px] tracking-[0.2em] uppercase text-zinc-600 py-4 border-t border-zinc-900"
          style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
        >
          {companyName ? `${companyName} © 2026` : "© 2026"}
        </footer>
        )}
      </main>

      {/* Owner-Fix (05.09.2026): floating Entsperren-Button + zugehöriges
          Passwort-Modal wurden entfernt. Der einzige Lock/Unlock-Control
          lebt neben dem „KASSE"-Header in POS.jsx. */}
    </div>
  );
}
